import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarPermissions } from '../contexts/SidebarPermissionsContext';
import Layout from '../components/Layout';
import api from '../services/api';
import { 
  Save, 
  RefreshCw,
  Building2,
  Users,
  Shield,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const SidebarPermissions = () => {
  const { user } = useAuth();
  const { permissions, updatePermissions, loadPermissions } = useSidebarPermissions();
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [localPermissions, setLocalPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Itens da sidebar que podem ter permissões configuradas
  const sidebarItems = [
    { key: 'dashboard', label: 'Dashboard', defaultRoles: ['admin', 'manager', 'common_user', 'viewer'] },
    { key: 'profile', label: 'Meu Perfil', defaultRoles: ['admin', 'manager', 'common_user', 'viewer'] },
    { key: 'clients', label: 'Clientes', defaultRoles: ['admin', 'manager', 'common_user', 'viewer'] },
    { key: 'settings', label: 'Configurações', defaultRoles: ['admin'] }
  ];

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      return;
    }
  }, [user]);

  // Carregar dados
  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user, permissions]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCompanies(),
        loadRoles(),
        loadLocalPermissions()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      // Usar a instância da API configurada
      const response = await api.get('/organizational/companies');
      
      if (response.data.companies) {
        setCompanies(response.data.companies);
      } else {
        toast.error('Formato de resposta inválido da API');
        setCompanies([]);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      
      if (error.response) {
        // Erro da API
        toast.error(`Erro ao carregar empresas: ${error.response.data?.error || 'Erro desconhecido'}`);
      } else if (error.request) {
        // Erro de rede
        toast.error('Erro de conexão com o servidor');
      } else {
        // Outro erro
        toast.error('Erro ao carregar empresas');
      }
      
      // Em caso de erro, usar dados mock para demonstração
      setCompanies([
        { id: 1, name: 'Empresa A' },
        { id: 2, name: 'Empresa B' },
        { id: 3, name: 'Empresa C' }
      ]);
    }
  };

  const loadRoles = async () => {
    try {
      // Usar roles padrão do sistema
      setRoles([
        { id: 'admin', name: 'Administrador', description: 'Acesso total ao sistema' },
        { id: 'manager', name: 'Gerente', description: 'Operações dos produtos e parâmetros de faturamento' },
        { id: 'common_user', name: 'Usuário Comum', description: 'Operações dos produtos, sem parâmetros nem configurações' },
        { id: 'viewer', name: 'Visualizador', description: 'Dashboard, perfil e área de clientes' }
      ]);
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
    }
  };

  const loadLocalPermissions = async () => {
    try {
      console.log('🔄 Carregando permissões locais...');
      
      // Para cada empresa, carregar permissões do banco
      const contextPermissions = {};
      
      for (const company of companies) {
        try {
          console.log(`📡 Carregando permissões para empresa ${company.id} (${company.name})...`);
          
          // Usar a nova rota que retorna TODAS as permissões
          const response = await api.get(`/sidebar-permissions/${company.id}/all`);
          
          if (response.data.success) {
            console.log(`✅ Permissões carregadas para empresa ${company.id}:`, response.data.permissions);
            
            // Converter formato de objeto para array (compatibilidade)
            const convertedPermissions = {};
            Object.entries(response.data.permissions).forEach(([itemKey, rolePermissions]) => {
              convertedPermissions[itemKey] = Object.entries(rolePermissions)
                .filter(([role, isAllowed]) => isAllowed)
                .map(([role]) => role);
            });
            
            contextPermissions[company.id] = convertedPermissions;
          } else {
            console.log(`⚠️ Erro ao carregar permissões para empresa ${company.id}, usando padrões`);
            contextPermissions[company.id] = {};
            sidebarItems.forEach(item => {
              contextPermissions[company.id][item.key] = item.defaultRoles;
            });
          }
        } catch (error) {
          console.log(`❌ Erro ao carregar permissões para empresa ${company.id}:`, error);
          // Usar permissões padrão em caso de erro
          contextPermissions[company.id] = {};
          sidebarItems.forEach(item => {
            contextPermissions[company.id][item.key] = item.defaultRoles;
          });
        }
      }
      
      console.log('✅ Permissões locais configuradas:', contextPermissions);
      setLocalPermissions(contextPermissions);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
    }
  };

  const handlePermissionChange = (companyId, itemKey, roleId, checked) => {
    setLocalPermissions(prev => {
      const newPermissions = { ...prev };
      
      if (!newPermissions[companyId]) {
        newPermissions[companyId] = {};
      }
      
      if (!newPermissions[companyId][itemKey]) {
        newPermissions[companyId][itemKey] = [];
      }
      
      if (checked) {
        // Adicionar role se não existir
        if (!newPermissions[companyId][itemKey].includes(roleId)) {
          newPermissions[companyId][itemKey] = [...newPermissions[companyId][itemKey], roleId];
        }
      } else {
        // Remover role
        newPermissions[companyId][itemKey] = newPermissions[companyId][itemKey].filter(id => id !== roleId);
      }
      
      return newPermissions;
    });
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      
      // Preparar permissões para envio (formato esperado pelo backend)
      // Enviar apenas as permissões da empresa do usuário logado
      const userCompanyId = user?.companyId;
      if (!userCompanyId) {
        toast.error('Usuário não tem empresa associada');
        return;
      }
      
      const permissionsToSend = {};
      permissionsToSend[userCompanyId] = {};
      
      sidebarItems.forEach(item => {
        // Enviar apenas as permissões que estão habilitadas (checkboxes marcados)
        const enabledRoles = localPermissions[userCompanyId]?.[item.key] || [];
        permissionsToSend[userCompanyId][item.key] = enabledRoles;
      });
      
      console.log('📤 Enviando permissões para o backend:', permissionsToSend);
      
      // Salvar permissões usando o contexto
      const result = await updatePermissions(permissionsToSend);
      
      if (result.success) {
        toast.success('Permissões salvas com sucesso!');
        // Recarregar permissões locais para refletir as mudanças
        await loadLocalPermissions();
      } else {
        toast.error(result.error || 'Erro ao salvar permissões');
      }
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      toast.error('Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    const defaultPermissions = {};
    
    companies.forEach(company => {
      defaultPermissions[company.id] = {};
      sidebarItems.forEach(item => {
        defaultPermissions[company.id][item.key] = [...item.defaultRoles];
      });
    });
    
    setLocalPermissions(defaultPermissions);
    toast.success('Permissões resetadas para os valores padrão');
  };

  const getPermissionStatus = (companyId, itemKey, roleId) => {
    // Verificar se temos permissões carregadas para esta empresa e item
    if (localPermissions[companyId] && localPermissions[companyId][itemKey]) {
      // Se temos permissões específicas, verificar se o role está incluído
      return localPermissions[companyId][itemKey].includes(roleId);
    }
    
    // Se não temos permissões específicas, usar padrões
    const defaultRoles = sidebarItems.find(item => item.key === itemKey)?.defaultRoles || [];
    return defaultRoles.includes(roleId);
  };

  // Verificar se o usuário é administrador
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Acesso Negado</h1>
          <p className="text-gray-600">Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Permissões da Sidebar</h1>
          <p className="text-gray-600">Configure quais usuários podem ver cada item da barra lateral baseado na empresa e perfil</p>
          {user?.isSuperAdmin && (
            <div className="mt-2 p-2 bg-primary-50 border border-primary-200 rounded-lg">
              <p className="text-sm text-primary-800">
                👑 <strong>Super Administrador</strong> - Você pode configurar permissões de todas as empresas
              </p>
            </div>
          )}
        </div>

        {/* Ações */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="h-4 w-4" />
              <span>Configure as permissões de acesso para cada empresa e perfil de usuário</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={resetToDefaults}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Resetar Padrões
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Salvando...' : 'Salvar Permissões'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabela de Permissões */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item da Sidebar
                  </th>
                  {companies.map(company => (
                    <th key={company.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex flex-col items-center">
                        <Building2 className="h-4 w-4 mb-1" />
                        <span className="text-xs">{company.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sidebarItems.map((item) => (
                  <tr key={item.key} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.label}</div>
                      <div className="text-xs text-gray-500">
                        Padrão: {item.defaultRoles.map(role => 
                          roles.find(r => r.id === role)?.name
                        ).join(', ')}
                      </div>
                    </td>
                    {companies.map(company => (
                      <td key={company.id} className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {roles.map(role => (
                            <div key={role.id} className="flex items-center justify-center">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={getPermissionStatus(company.id, item.key, role.id)}
                                  onChange={(e) => handlePermissionChange(
                                    company.id, 
                                    item.key, 
                                    role.id, 
                                    e.target.checked
                                  )}
                                  className="sr-only"
                                />
                                <div className={`
                                  w-5 h-5 border-2 rounded flex items-center justify-center transition-colors
                                  ${getPermissionStatus(company.id, item.key, role.id)
                                    ? 'bg-primary-600 border-primary-600'
                                    : 'border-gray-300 hover:border-gray-400'
                                  }
                                `}>
                                  {getPermissionStatus(company.id, item.key, role.id) && (
                                    <Check className="h-3 w-3 text-white" />
                                  )}
                                </div>
                                <span className="ml-2 text-xs text-gray-600">{role.name}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legenda */}
        <div className="mt-6 bg-primary-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-primary-900 mb-2">Como usar:</h3>
          <ul className="text-sm text-primary-800 space-y-1">
            <li>• Marque as caixas para permitir que usuários com determinado perfil vejam o item da sidebar</li>
            <li>• Desmarque para restringir o acesso</li>
            <li>• As permissões são aplicadas por empresa, permitindo configurações diferentes para cada organização</li>
            <li>• Use "Resetar Padrões" para voltar às configurações iniciais do sistema</li>
          </ul>
        </div>

        {/* Informações dos Perfis */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Perfis de Usuário:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {roles.map(role => (
              <div key={role.id} className="bg-white p-3 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900">{role.name}</h4>
                <p className="text-sm text-gray-600">{role.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SidebarPermissions;
